import { FormEvent, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authClient } from "../lib/auth-client";
import { API_URL } from "../lib/api-client";
import { isFieldApp } from "../lib/field-app";

type AuthMode = "sign-in" | "sign-up";

const fieldInputClass =
  "mt-0 h-12 w-full rounded-xl border border-[#e2e2e2] bg-white px-3.5 text-base text-black outline-none placeholder:text-[#b0b0b0] focus:border-black";

async function postAuth(path: string, body: Record<string, string>) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => null) as { message?: string; error?: { message?: string } } | null;
    if (!response.ok) throw new Error(payload?.message || payload?.error?.message || (path.includes("sign-up") ? "Unable to create that account." : "Unable to log in with those details."));
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error(path.includes("sign-up") ? "Account creation took too long. Check your connection and try again." : "Login took too long. Check your connection and try again.");
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

function PasswordRecovery({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await postAuth("/api/auth/request-password-reset", { email: email.trim(), redirectTo: `${window.location.origin}/auth/reset-password` });
      setMessage("If that email belongs to a WorkView account, we sent a password link.");
    } catch {
      setMessage("We couldn’t send a reset email. Try again in a few minutes.");
    }
    finally { setSubmitting(false); }
  }
  return <main className="mx-auto flex min-h-[100dvh] max-w-md items-center px-6"><section className="w-full rounded-2xl border border-slate-200 bg-white p-6"><h1 className="text-2xl font-semibold text-slate-950">Reset your password</h1><p className="mt-2 text-sm text-slate-600">We’ll email a secure one-time link.</p><form onSubmit={submit} className="mt-6 space-y-4"><input required type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@example.com" className="w-full rounded-lg border border-slate-300 px-3.5 py-3" />{message && <p role="status" className="rounded-lg bg-slate-100 px-3 py-3 text-sm text-slate-700">{message}</p>}<button disabled={submitting} className="w-full rounded-lg bg-[var(--accent)] px-4 py-3 font-semibold text-white">{submitting ? "Sending…" : "Email reset link"}</button></form><button type="button" onClick={onBack} className="mt-5 text-sm font-semibold text-slate-600">← Back to sign in</button></section></main>;
}

function ResetPassword({ token, fieldApp }: { token: string | null; fieldApp: boolean }) {
  const [password, setPassword] = useState(""); const [confirm, setConfirm] = useState(""); const [error, setError] = useState(""); const [complete, setComplete] = useState(false); const [submitting, setSubmitting] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError("");
    if (!token) return setError("This password link is invalid or expired.");
    if (password.length < 8) return setError("Your password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setSubmitting(true);
    try { await postAuth("/api/auth/reset-password", { newPassword: password, token }); setComplete(true); }
    catch (cause) { setError(cause instanceof Error ? cause.message : "This password link is invalid or expired."); }
    finally { setSubmitting(false); }
  }
  const destination = fieldApp ? "/auth" : "/auth";
  return <main className="mx-auto flex min-h-[100dvh] max-w-md items-center px-6"><section className="w-full rounded-2xl border border-slate-200 bg-white p-6"><h1 className="text-2xl font-semibold text-slate-950">Choose a new password</h1>{complete ? <><p role="status" className="mt-4 text-sm text-slate-700">Your password has been updated. Sign in to continue.</p><a href={destination} className="mt-6 inline-block rounded-lg bg-[var(--accent)] px-4 py-3 font-semibold text-white">Sign in</a></> : <form onSubmit={submit} className="mt-6 space-y-4"><input required type="password" autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="New password" className="w-full rounded-lg border border-slate-300 px-3.5 py-3" /><input required type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} placeholder="Confirm password" className="w-full rounded-lg border border-slate-300 px-3.5 py-3" />{error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-3 text-sm text-red-700">{error}</p>}<button disabled={submitting} className="w-full rounded-lg bg-[var(--accent)] px-4 py-3 font-semibold text-white">{submitting ? "Updating…" : "Update password"}</button></form>}</section></main>;
}

export function FieldSignIn() {
  const { data: session, isPending } = authClient.useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recovering, setRecovering] = useState(false);
  const canSubmit = Boolean(email.trim() && password) && !isSubmitting;

  if (!isPending && session?.user) {
    return <Navigate to="/technician" replace />;
  }
  if (recovering) return <PasswordRecovery onBack={() => setRecovering(false)} />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Enter the email address for your Workview account.");
      return;
    }
    if (!password) {
      setError("Enter your password to log in.");
      return;
    }
    if (password.length < 8) {
      setError("Your password must be at least 8 characters.");
      return;
    }
    setIsSubmitting(true);
    try {
      await postAuth("/api/auth/sign-in/email", { email: email.trim(), password });
      window.location.assign("/technician");
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "We couldn’t reach the authentication service. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-white px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-[max(1.5rem,env(safe-area-inset-top))]">
      <section className="mx-auto w-full max-w-[430px] pt-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-[#f5c400]" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12.5 9.5 17 19 7" />
          </svg>
        </div>
        <h1 className="mt-5 text-[34px] font-semibold leading-none tracking-tight text-black">Workview Pro</h1>
        <p className="mt-2 text-[17px] text-[#8a8a8a]">Technician sign in</p>
        <form noValidate onSubmit={handleSubmit} className="mt-8 space-y-3">
          <label className="sr-only" htmlFor="field-email">Email</label>
          <input id="field-email" type="email" autoComplete="email" inputMode="email" value={email} onChange={(event) => setEmail(event.target.value)} className={fieldInputClass} placeholder="Email" />
          <label className="sr-only" htmlFor="field-password">Password</label>
          <input id="field-password" type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} className={fieldInputClass} placeholder="Password" />
          {error && <p role="alert" className="rounded-xl bg-red-50 px-3.5 py-3 text-sm text-red-700">{error}</p>}
          <button
            disabled={!canSubmit}
            type="submit"
            className={`mt-2 min-h-12 w-full rounded-full text-[17px] font-semibold ${canSubmit ? "bg-[#d0d0d0] text-black" : "bg-[#ececec] text-white"} disabled:cursor-not-allowed`}
          >
            {isSubmitting ? "Please wait…" : "Sign In"}
          </button>
          <button type="button" onClick={() => setRecovering(true)} className="w-full pt-2 text-sm font-semibold text-[#555]">Forgot password?</button>
        </form>
      </section>
    </main>
  );
}

export default function AuthPage() {
  const location = useLocation();
  const fieldApp = isFieldApp(undefined, location.search);
  const { data: session, isPending } = authClient.useSession();
  const user = session?.user;
  const mode: AuthMode = new URLSearchParams(location.search).get("mode") === "sign-up" ? "sign-up" : "sign-in";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    setError("");
    setRecovering(false);
  }, [mode]);

  if (location.pathname.endsWith("/reset-password")) return <ResetPassword token={new URLSearchParams(location.search).get("token")} fieldApp={fieldApp} />;
  if (fieldApp) return <FieldSignIn />;

  if (!isPending && user) {
    return <Navigate to="/dashboard" replace />;
  }
  if (recovering) return <PasswordRecovery onBack={() => setRecovering(false)} />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");

    if (mode === "sign-up" && !name.trim()) {
      setError("Enter your name to create an account.");
      return;
    }
    if (!email.trim()) {
      setError("Enter the email address for your Workview account.");
      return;
    }
    if (!password) {
      setError("Enter your password to log in.");
      return;
    }
    if (password.length < 8) {
      setError("Your password must be at least 8 characters.");
      return;
    }
    setIsSubmitting(true);

    try {
      if (mode === "sign-in") {
        await postAuth("/api/auth/sign-in/email", { email: email.trim(), password });
        window.location.assign("/dashboard");
        return;
      }

      await postAuth("/api/auth/sign-up/email", { name: name.trim(), email: email.trim(), password });
      window.location.assign("/dashboard");
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "We couldn’t reach the authentication service. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="mx-auto flex max-w-6xl justify-center px-6 py-14 sm:px-10 sm:py-20">
      <section className="w-full max-w-md">
        <div className="mb-8 text-center">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[var(--accent)]">Welcome to Workview</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">
            {mode === "sign-in" ? "Log in to your workspace" : "Create your workspace account"}
          </h1>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          <form noValidate onSubmit={handleSubmit} className="space-y-5">
            {mode === "sign-up" && <label className="block text-sm font-medium text-slate-700">Name<input value={name} onChange={(event) => setName(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20" placeholder="Ada Lovelace" /></label>}
            <label className="block text-sm font-medium text-slate-700">Email address<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20" placeholder="you@example.com" /></label>
            <label className="block text-sm font-medium text-slate-700">Password<input type="password" autoComplete={mode === "sign-in" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} className="mt-2 w-full rounded-lg border border-slate-300 px-3.5 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20" placeholder="At least 8 characters" /></label>
            {error && <p role="alert" className="rounded-lg bg-red-50 px-3.5 py-3 text-sm text-red-700">{error}</p>}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button disabled={isSubmitting} type="submit" className="auth-action-chip inline-flex min-h-11 items-center justify-center rounded-full border px-6 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60">
                {isSubmitting ? "Please wait…" : mode === "sign-in" ? "Log in" : "Create account"}
              </button>
              {mode === "sign-in" && <button type="button" onClick={() => setRecovering(true)} className="auth-action-chip inline-flex min-h-11 items-center justify-center rounded-full border px-6 py-2.5 text-sm font-semibold transition-colors">Forgot password?</button>}
            </div>
          </form>
        </div>
        <p className="mt-6 text-center text-xs leading-5 text-slate-500">By continuing, you agree to use Workview Pro responsibly and keep your account secure.</p>
      </section>
    </main>
  );
}
